import APICall from "../../utils/APICalls.js";
import responseModel from "../../models/Generic/responseModel.js";
import MediaModel from "../../models/Application/mediaModel.js";

const MediaServiceClient = {
  /**
   * Create Media
   * POST /api/v1/Media
   * @param {MediaModel|Object} mediaDto - Media data to create
   * @returns {Promise<responseModel>} Response with MediaResultDto
   */
  create: async function (mediaDto) {
    try {
      // Ensure we're sending the right data structure
      const payload =
        mediaDto instanceof MediaModel
          ? {
              id: null,
              name: mediaDto.name,
              type: mediaDto.type,
              status: mediaDto.status,
              boundEntityType: mediaDto.boundEntityType,
              contentType: mediaDto.contentType,
              contentLength: mediaDto.contentLength,
              cloudKey: mediaDto.cloudKey,
              url: mediaDto.url,
              createdAt: mediaDto.createdAt,
              createdByEmail: mediaDto.createdByEmail,
            }
          : mediaDto;

      const result = await APICall.post(`Media`, payload);

      if (result !== null) {
        if (result.succeeded === true && result.object) {
          return responseModel.responseModelOk(result.object);
        } else {
          return new responseModel(
            false,
            null,
            result.errors && result.errors.length > 0
              ? result.errors[0].description
              : "There is a problem creating the media."
          );
        }
      } else {
        return new responseModel(
          false,
          null,
          "There is a problem with the API service."
        );
      }
    } catch (error) {
      return new responseModel(
        false,
        null,
        "There is a problem creating the media."
      );
    }
  },

  /**
   * Complete Media Upload
   * POST /api/v1/Media/{mediaId}/complete
   * @param {string} mediaId - The media ID (GUID)
   * @returns {Promise<responseModel>} Response with MediaDto
   */
  completeUpload: async function (mediaId) {
    try {
      const result = await APICall.post(`Media/${mediaId}/complete`);

      if (result !== null) {
        if (result.succeeded === true && result.object) {
          // Convert the response to MediaModel
          const mediaModel = new MediaModel(result.object);
          return responseModel.responseModelOk(mediaModel);
        } else {
          return new responseModel(
            false,
            null,
            result.errors && result.errors.length > 0
              ? result.errors[0].description
              : "There is a problem completing the media upload."
          );
        }
      } else {
        return new responseModel(
          false,
          null,
          "There is a problem with the API service."
        );
      }
    } catch (error) {
      return new responseModel(
        false,
        null,
        "There is a problem completing the media upload."
      );
    }
  },

  /**
   * Download Media
   * GET /api/v1/Media/{mediaId}/download
   * @param {string} mediaId - The media ID (GUID)
   * @returns {Promise<responseModel>} Response with DownloadMediaResultDto
   */
  download: async function (mediaId) {
    try {
      const result = await APICall.get(`Media/${mediaId}/download`);

      if (result !== null) {
        if (result.succeeded === true && result.object) {
          return responseModel.responseModelOk(result.object);
        } else {
          return new responseModel(
            false,
            null,
            result.errors && result.errors.length > 0
              ? result.errors[0].description
              : "There is a problem downloading the media."
          );
        }
      } else {
        return new responseModel(
          false,
          null,
          "There is a problem with the API service."
        );
      }
    } catch (error) {
      return new responseModel(
        false,
        null,
        "There is a problem downloading the media."
      );
    }
  },

  /**
   * Get Media Events
   * GET /api/v1/Media/{mediaId}/events
   * @param {string} mediaId - The media ID (GUID)
   * @returns {Promise<responseModel>} Response with IEnumerable<MediaEventDto>
   */
  getEvents: async function (mediaId) {
    try {
      const result = await APICall.get(`Media/${mediaId}/events`);

      if (result !== null) {
        if (result.succeeded === true && result.object) {
          return responseModel.responseModelOk(result.object);
        } else {
          return new responseModel(
            false,
            null,
            result.errors && result.errors.length > 0
              ? result.errors[0].description
              : "There is a problem getting the media events."
          );
        }
      } else {
        return new responseModel(
          false,
          null,
          "There is a problem with the API service."
        );
      }
    } catch (error) {
      return new responseModel(
        false,
        null,
        "There is a problem getting the media events."
      );
    }
  },
};

export default MediaServiceClient;
